import type { UsageRow, UsageStatus } from './types';

// Pure usage presentation logic — no React, no fetch. Kept separate so the thresholds,
// percentages, cycle math, and Pacific formatting are unit-testable in isolation.

export const WARN_PCT = 80;
export const OVER_PCT = 100;

/** Percent of a limit consumed. A zero/absent limit yields 0 (avoids divide-by-zero and
 *  a misleading 100% on an unmetered resource). Not clamped — callers clamp the bar width. */
export function pct(used: number, limit: number): number {
  if (!limit || limit <= 0) return 0;
  return (used / limit) * 100;
}

export function minutes_pct(row: UsageRow): number {
  return pct(row.used.minutes, row.limits.minutes);
}

export function messages_pct(row: UsageRow): number {
  return pct(row.used.messages, row.limits.messages);
}

/** The tenant's headline consumption = the more-consumed of the two metered resources.
 *  Drives the status badge and the default table sort. */
export function consumed_pct(row: UsageRow): number {
  return Math.max(minutes_pct(row), messages_pct(row));
}

/** ok / warn (≥80%) / over (≥100%). Honors the upstream `over_*` flags too, so a hard-gated
 *  tenant reads `over` even if rounding leaves the percent a hair under 100. */
export function status_of(row: UsageRow): UsageStatus {
  if (row.over_minutes || row.over_messages || consumed_pct(row) >= OVER_PCT) return 'over';
  if (consumed_pct(row) >= WARN_PCT) return 'warn';
  return 'ok';
}

export function status_color(status: UsageStatus): string {
  switch (status) {
    case 'over':
      return '#b3261e';
    case 'warn':
      return '#b58900';
    default:
      return 'var(--green-700)';
  }
}

export function status_label(status: UsageStatus): string {
  return status; // 'ok' | 'warn' | 'over' — the spec's literal labels
}

/** Whole days remaining until the cycle end, never negative. Ceil so a partial day still
 *  reads as "1 day left" rather than 0 until the instant it actually ends. */
export function days_remaining(cycle_end_iso: string, now: Date = new Date()): number {
  const end = new Date(cycle_end_iso).getTime();
  if (Number.isNaN(end)) return 0;
  const ms = end - now.getTime();
  if (ms <= 0) return 0;
  return Math.ceil(ms / 86_400_000);
}

/** Format a UTC instant as a Pacific calendar date (e.g. "May 30, 2026"). The cycle window
 *  is UTC-anchored in storage; we only present it in PT per the spec. */
export function fmt_pacific_date(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d);
}

/** "May 30, 2026 → Jun 30, 2026 PT" */
export function fmt_cycle_window(cycle: { start: string; end: string }): string {
  return `${fmt_pacific_date(cycle.start)} → ${fmt_pacific_date(cycle.end)} PT`;
}

export function fmt_pct(n: number): string {
  return `${Math.round(n)}%`;
}

export type SortDir = 'asc' | 'desc';

/** Sort rows by headline consumption. Default desc = most-consumed first (the spec default). */
export function sort_by_consumed(rows: UsageRow[], dir: SortDir = 'desc'): UsageRow[] {
  const factor = dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => (consumed_pct(a) - consumed_pct(b)) * factor);
}
